<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { Diagnostic } from "@/types";
import PdfIcon from "@/components/UI/icons/PdfIcon.vue";
import DocIcon from "@/components/UI/icons/DocIcon.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Rapport de l'inspection précédente, en version compacte pour l'en-tête de la
 * page ouvrage. Les actions (télécharger, analyser, remplacer) vivent dans le
 * menu d'options de l'ouvrage ; ici on ne montre que l'état.
 */

const props = defineProps<{
  diagnostic: Diagnostic;
}>();

const emit = defineEmits<{
  (e: "import"): void;
}>();

const { t } = useTranslation();

const file = computed(() => props.diagnostic.document);

const isPdf = computed(() =>
  (file.value?.originalName ?? "").toLowerCase().endsWith(".pdf")
);

const isExtracting = computed(() => props.diagnostic.isExtractingData);
</script>

<template>
  <!-- Rapport présent -->
  <el-tooltip
    v-if="file"
    :content="file.originalName"
    effect="light"
    placement="bottom"
  >
    <div class="report-chip" :class="{ extracting: isExtracting }">
      <i v-if="isExtracting" class="bi-stars pulse-icon" />
      <PdfIcon v-else-if="isPdf" :size="18" class="flex-shrink-0" />
      <DocIcon v-else :size="18" class="flex-shrink-0" />
      <div class="chip-text">
        <span class="chip-label">{{ t("report:past.report") }}</span>
        <span class="chip-name">
          {{
            isExtracting
              ? t("report:diagnostics.extracting.data")
              : file.originalName
          }}
        </span>
      </div>
    </div>
  </el-tooltip>

  <!-- Aucun rapport : on attire l'œil, c'est la matière première de l'IA -->
  <button v-else type="button" class="report-chip empty" @click="emit('import')">
    <PhosphorIcon name="file-arrow-up" :size="18" weight="bold" />
    <div class="chip-text">
      <span class="chip-label">{{ t("report:past.report") }}</span>
      <span class="chip-name">
        {{ t("report:diagnostics.noPastReport") }}
      </span>
    </div>
    <span class="chip-cta">{{
      t("report:diagnostics.noPastReportAction")
    }}</span>
  </button>
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

.report-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  min-width: 0; /* sans ça, l'ellipsis ne se déclenche pas en flex */
  padding: 4px 10px;
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
  background-color: var(--bs-body-bg);
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &.extracting {
    background: linear-gradient(135deg, #8b5cf6 0%, $ia 100%);
    border-color: transparent;
    color: white;
  }

  &.empty {
    border-style: dashed;
    border-color: var(--bs-warning);
    color: var(--bs-body-color);
    cursor: pointer;

    &:hover {
      background-color: rgba(var(--bs-warning-rgb), 0.1);
    }
  }
}

.chip-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.chip-label {
  font-size: 10px;
  opacity: 0.6;
}

.chip-name {
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-cta {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--bs-warning-text-emphasis, var(--bs-warning));
}

.pulse-icon {
  font-size: 16px;
  flex-shrink: 0;
  animation: pulse-stars 2s ease-in-out infinite;
}

@keyframes pulse-stars {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
